import { useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { LookupRequest, LookupResponse } from "@pm-barcode/shared";
import { useApiClient } from "../hooks/use-api-client.ts";
import { useScanner } from "../hooks/use-scanner.ts";

// ─── Quality status badge ────────────────────────────────────────────────────

const QUALITY_COLOURS: Record<string, string> = {
  ready: "bg-green-700 text-green-100",
  quarantined: "bg-amber-700 text-amber-100",
  hold: "bg-red-700 text-red-100",
  return: "bg-orange-700 text-orange-100",
};

function QualityBadge({ status }: { status: string | null }) {
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : "Unknown";
  const colour = (status && QUALITY_COLOURS[status.toLowerCase()]) ?? "bg-gray-700 text-gray-300";
  return (
    <span className={`rounded-full px-3 py-0.5 text-sm font-semibold ${colour}`}>
      {label}
    </span>
  );
}

// ─── Result card ─────────────────────────────────────────────────────────────

function ResultCard({ result }: { result: LookupResponse }) {
  if (result.outcome === "unknown") {
    return (
      <div className="rounded-2xl bg-gray-800 p-5">
        <p className="text-lg font-bold text-scan-error">Unknown Barcode</p>
        <p className="mt-2 text-sm text-gray-400">
          This barcode was not recognised. It has been logged for review.
        </p>
        <p className="mt-3 break-all font-mono text-xs text-gray-600">{result.rawBarcode}</p>
      </div>
    );
  }

  if (result.outcome === "not_in_odoo") {
    const n = result.normalised;
    return (
      <div className="rounded-2xl bg-gray-800 p-5">
        <p className="text-lg font-bold text-amber-400">Not Yet in Stock</p>
        <p className="mt-1 text-sm text-gray-400">
          Barcode decoded but this bag has not been received into Odoo yet.
        </p>
        <dl className="mt-4 space-y-2 text-sm">
          <Row label="Category" value={n.category.replace(/_/g, " ")} />
          <Row label="Batch" value={n.batch} />
          {n.bagNumber && <Row label="Bag" value={n.bagNumber} />}
          <Row label="Weight" value={`${n.weightKg} kg`} />
          {n.bestBefore && <Row label="Best before" value={n.bestBefore} />}
        </dl>
      </div>
    );
  }

  // outcome === "found"
  const n = result.normalised;
  const { product, lot, locations } = result;

  return (
    <div className="rounded-2xl bg-gray-800 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <p className="text-lg font-bold leading-tight">{product.name}</p>
        <QualityBadge status={lot.qualityStatus} />
      </div>

      <dl className="space-y-2 text-sm">
        <Row label="Lot / serial" value={lot.name} mono />
        <Row label="Batch" value={n.batch} />
        {n.bagNumber && <Row label="Bag" value={n.bagNumber} />}
        <Row label="Weight" value={`${n.weightKg} kg`} />
        {n.bestBefore && <Row label="Best before" value={n.bestBefore} />}
        {lot.expiryDate && <Row label="Expiry" value={lot.expiryDate.split("T")[0] ?? lot.expiryDate} />}
      </dl>

      <div className="mt-4 border-t border-gray-700 pt-4">
        <p className="mb-2 text-xs uppercase tracking-widest text-gray-500">Stock location</p>
        {locations.length === 0 ? (
          <p className="text-sm text-gray-400">Not currently in stock</p>
        ) : (
          <ul className="space-y-1">
            {locations.map((loc) => (
              <li key={loc.id} className="flex justify-between text-sm">
                <span className="font-mono text-gray-300">{loc.name}</span>
                <span className="tabular-nums text-gray-400">{loc.quantity}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-right ${mono ? "font-mono" : ""} text-gray-200`}>{value}</dd>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BagLookup() {
  const navigate = useNavigate();
  const api = useApiClient();
  const [result, setResult] = useState<LookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const scanInFlight = useRef(false);

  useScanner((event) => {
    if (!api || scanInFlight.current) return;
    scanInFlight.current = true;
    setLoading(true);
    setResult(null);

    const body: LookupRequest = {
      rawBarcode: event.rawBarcode,
      symbology: event.symbology,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: event.capturedAt,
    };

    void api
      .postLookup(body)
      .then((r) => setResult(r))
      .catch(() => {
        setResult({
          outcome: "unknown",
          rawBarcode: event.rawBarcode,
          unknownId: "00000000-0000-0000-0000-000000000000",
        });
      })
      .finally(() => {
        setLoading(false);
        scanInFlight.current = false;
      });
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/workflow" })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="flex-1">
          <p className="font-semibold">Bag Lookup</p>
          <p className="text-xs text-gray-500">Scan any bag to see its details</p>
        </div>
        <span className="rounded-full bg-amber-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-amber-300">
          Lookup
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-gray-400">Looking up…</p>
          </div>
        ) : result ? (
          <ResultCard result={result} />
        ) : (
          <div className="flex h-40 items-center justify-center">
            <p className="text-sm text-gray-500">Scan a bag to see its details</p>
          </div>
        )}
      </div>
    </div>
  );
}
